/**
 * Flow editor — lightweight gradient slider for volume knobs.
 *
 * Same interaction model as the SR editor's GradientSlider (mousedown +
 * window-level mousemove for drag), but doesn't read from interfaceSettings
 * so it stays self-contained inside the Flow editor.
 */

import React, { useCallback, useRef } from 'react';
import { color } from '../../components/ui/theme';
import { useEditorStore } from '../../store/editorStore';

interface Props {
  label: string;
  /** Current value. Defaults to 0..1 range unless min/max are provided. */
  value: number;
  onChange: (v: number) => void;
  /** @deprecated Ignored — sliders use the accent theme tokens. Kept so old
   *  call sites still typecheck; remove freely. */
  gradientFrom?: string;
  /** @deprecated Ignored — see {@link gradientFrom}. */
  gradientTo?: string;
  /** Minimum value (default 0). */
  min?: number;
  /** Maximum value (default 1). */
  max?: number;
  /** Snap step (default 0.01). */
  step?: number;
  /** Format function for the value label. */
  format?: (v: number) => string;
}

export default function FlowSlider({
  label, value, onChange,
  min = 0, max = 1, step = 0.01,
  format,
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  // Unified slider look: same track/thumb appearance AND gradient as the
  // Mixer's GradientSlider (thicker track, ring thumb with a coloured-border +
  // centre dot). The gradient is shared app-wide via interfaceSettings — the
  // per-slider gradientFrom/To props are intentionally ignored.
  const si = useEditorStore(s => s.interfaceSettings);
  // Gradient + thumb track the ACCENT theme tokens (dim→bright), matching the
  // Mixer's GradientSlider so every slider re-tints with the chosen accent.
  // The per-slider gradientFrom/To props and the stored gradient colours are
  // intentionally ignored — the design system owns slider colour now. Only the
  // sizing/track styling still comes from interfaceSettings.
  const gradientFrom = 'var(--accent-dim)';
  const gradientTo = 'var(--accent)';

  // Rect captured at mousedown. If a parent re-render swaps the track node out
  // mid-drag (React remounts the subtree), trackRef.current goes null and the
  // drag would otherwise measure nothing and freeze — fall back to this.
  const dragRect = useRef<DOMRect | null>(null);

  const posToValue = useCallback((clientX: number): number => {
    const r = trackRef.current?.getBoundingClientRect() ?? dragRect.current;
    if (!r) return value;
    const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    const raw = min + ratio * (max - min);
    const snapped = Math.round(raw / step) * step;
    return parseFloat(Math.max(min, Math.min(max, snapped)).toFixed(10));
  }, [min, max, step, value]);

  const handleMove = useCallback((clientX: number) => {
    onChange(posToValue(clientX));
  }, [onChange, posToValue]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Prevent the browser from starting a text selection — otherwise the drag
    // is hijacked by the selection and only the initial click registers.
    e.preventDefault();
    dragRect.current = trackRef.current?.getBoundingClientRect() ?? null;
    handleMove(e.clientX);
    const onMove = (ev: MouseEvent) => { ev.preventDefault(); handleMove(ev.clientX); };
    const onUp = () => {
      dragRect.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [handleMove]);

  const range = max - min;
  const pct = range > 0 ? (((value - min) / range) * 100).toFixed(1) : '0';
  const displayValue = format ? format(value) : `${(value * 100).toFixed(0)}%`;

  return (
    <div style={wrapStyle}>
      <div style={headerRowStyle}>
        <div style={labelStyle}>{label}</div>
        {format !== undefined && <div style={valueStyle}>{displayValue}</div>}
      </div>
      <div
        ref={trackRef}
        style={{ ...trackStyle, height: si.sliderTrackHeight, background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)', borderColor: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)' }}
        onMouseDown={handleMouseDown}
      >
        <div style={{
          ...fillStyle,
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
        }} />
        <div style={{
          ...thumbStyle,
          width: si.sliderThumbSize,
          height: si.sliderThumbSize,
          left: `${pct}%`,
          background: 'var(--bg-1)',
          border: '2px solid var(--accent)',
          boxShadow: '0 0 8px var(--accent)',
        }} />
      </div>
      {format === undefined && (
        <div style={valueStyle}>{displayValue}</div>
      )}
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
};

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  letterSpacing: 1,
  color: color.inkFaint,
  textTransform: 'uppercase',
  fontWeight: 600,
};

const valueStyle: React.CSSProperties = {
  fontSize: 9,
  color: color.inkDim,
  fontFamily: 'monospace',
  letterSpacing: 0.3,
};

const trackStyle: React.CSSProperties = {
  position: 'relative',
  height: 6,
  borderRadius: 4,
  background: 'rgba(10, 5, 25, 0.6)',
  border: '1px solid rgba(120, 80, 220, 0.15)',
  cursor: 'pointer',
};

const fillStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  borderRadius: 4,
  transition: 'width 0.05s ease',
};

const thumbStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 14,
  height: 14,
  borderRadius: '50%',
  transition: 'left 0.05s ease',
};
